import React from "react";
import Slider from "react-slider";
import { ReactComponent as Chevron } from "../../../../../assets/icons/chevron.svg";
import { ReactComponent as BudgetIcon } from "../../../../../assets/icons/redesign_budget_icon.svg";
import getSymbolFromCurrency from "currency-symbol-map";
import styles from "./BudgetFilter.module.scss";
import { switches } from "../../ResultsList.utils";
import { useResultListContext } from "common/contexts/ResultListContext";

interface Props {
  fetchData: Function,
}

const BudgetFilter: React.FC<Props> = ({
  fetchData,
}) => {
  const { budgetValues, setBudgetValues, histogramValues, budgetRange, currentDuration, currentLocation, workspaceType, setSortBy } = useResultListContext();
  const [isFilterVisible, setIsFilterVisible] = React.useState(false);

  // @ts-ignore
  const currentCurrency = window?.hydrateJSON?.currencyCode || 'EUR';

  const toggleFilterVisibility = () => {
    setIsFilterVisible((prevIsFilterVisible) => !prevIsFilterVisible);
  };

  const handleRangeChange = (values: number[]) => {
    setBudgetValues(values);
    updatePrices(values[0], values[1]);
  };

  const handleMinInputChange = (value: number) => {
    if (value >= budgetRange[0] && value <= budgetValues[1]) {
      setBudgetValues([value, budgetValues[1]]);
    } else {
      setBudgetValues([budgetValues[1], budgetValues[1]])
    }
  };

  const handleMaxInputChange = (value: number) => {
    if (value <= budgetRange[1] && value >= budgetValues[0]) {
      setBudgetValues([budgetValues[0], value]);
    } else {
      setBudgetValues([budgetValues[0], budgetValues[0]])
    }
  };

  const handleMinInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (+e.currentTarget.value > budgetValues[1]) {
        setBudgetValues([budgetValues[1], budgetValues[1]]);
      } else if (+e.currentTarget.value < budgetRange[0]) {
        setBudgetValues([budgetRange[0], budgetValues[1]]);
      } else {
        handleMinInputChange(parseFloat(e.currentTarget.value));
        e.currentTarget.blur();
      }
      updatePrices(+e.currentTarget.value, budgetValues[1]);
    }
  };

  const handleMaxInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (+e.currentTarget.value < budgetValues[0]) {
        setBudgetValues([budgetValues[0], budgetValues[0]]);
      } else if (+e.currentTarget.value > budgetRange[1]) {
        setBudgetValues([budgetValues[0], budgetRange[1]]);
      } else {
        handleMaxInputChange(parseFloat(e.currentTarget.value));
        e.currentTarget.blur();
      }
      updatePrices(budgetValues[0], +e.currentTarget.value);
    }
  };

  React.useEffect(() => {
    if (
      budgetValues[0] < budgetRange[0] ||
      (budgetValues[1] > budgetRange[1] && budgetRange[1] !== 0) ||
      budgetValues[0] > budgetRange[1]
    ) {
      setBudgetValues([budgetRange[0], budgetRange[1]]);
    }
  }, [budgetRange]);

  const updatePrices = (minValue: number = budgetValues[0], maxValue: number = budgetValues[1]) => {
    const searchParams = new URLSearchParams(location.search);
    const pageFilter = searchParams.get('page') || 0;
    const isResetPage = minValue !== budgetRange[0] || maxValue !== budgetRange[1];
    if (minValue <= maxValue) {
        const searchParams = new URLSearchParams(location.search);
        const amenitiesFilter = '&amenities='+searchParams.get('amenities')?.replace(/^,+/g, '').replace(/,+/g, ',').replace(/,$/g, '').replace(/^undefined,|,undefined$|,undefined,/g, '').replace(/NaN|NaN,|,NaN$|,NaN,/g, '') || '';

        const sortFilter = searchParams.get('sort_by') || '';
        setSortBy(sortFilter);

        fetchData(
          currentLocation.lat,
          currentLocation.lng,
          currentLocation.rad,
          10,
          isResetPage ? 0 : +pageFilter,
          // @ts-ignore
          Object.entries(workspaceType).filter((el) => el[1] == true).map(item => switches[item[0]]),
          amenitiesFilter,
          currentDuration,
          [minValue, maxValue],
          sortFilter,
        );
    };
  }

  const handleBackgroundClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setIsFilterVisible(false);
  };

  return (
    <button className={isFilterVisible ? styles.container_active : styles.container} onClick={toggleFilterVisibility} type="button" style={{borderColor: budgetValues[0] !== budgetRange[0] || budgetValues[1] !== budgetRange[1] ? '#00c78b' : ''}}>
      <div className={styles.heading}>
        <div className={styles.toggle} style={{color: budgetValues[0] !== budgetRange[0] || budgetValues[1] !== budgetRange[1] ? '#00927c' : '' }} >
          <BudgetIcon />
          Budget
        </div>
        <Chevron style={{ transform: isFilterVisible ? 'rotate(180deg)' : 'none', color: budgetValues[0] !== budgetRange[0] || budgetValues[1] !== budgetRange[1] ? '#00c78b' : '' }} />
      </div>
      {isFilterVisible && (
        <div className={styles.background} onClick={handleBackgroundClick}>
          <div
            className={styles.container__filters}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className={styles.bs}>
              {Array.from({ length: 48 }, (_, i) => {
                const el = budgetRange[0] + (budgetRange[1] - budgetRange[0]) * (i / 47);

                return (
                  <div
                    key={i}
                    className={styles.bschild}
                    style={{
                      background:
                        Math.round(el) >= Math.round(budgetValues[0]) &&
                        Math.round(el) <= Math.round(budgetValues[1])
                          ? "#00C78A3D"
                          : "",
                      height: `${typeof histogramValues[i] == 'number' ? 26*histogramValues[i]/100 : 0}px`
                    }}
                  ></div>
                );
              })}
            </div>
            <Slider
              disabled={budgetRange[0] == budgetRange[1]}
              className={styles.slider}
              thumbClassName={styles.thumb}
              trackClassName={styles.track}
              defaultValue={budgetValues}
              value={budgetValues}
              ariaLabel={["Lower thumb", "Upper thumb"]}
              ariaValuetext={(state) => `Thumb value ${state.valueNow}`}
              renderThumb={(props) => <div {...props}></div>}
              pearling
              onAfterChange={handleRangeChange}
              minDistance={10}
              min={budgetRange[0]}
              max={budgetRange[1]}
              step={(budgetRange[1] - budgetRange[0]) / 47} // this and 'Math.round' in inputs below may probably cause some filter logic issues
              // doing this due to the figma design rectangles above slider
            />
            <div className={styles.range}>
              <div className={styles.min}>
                <span className={styles.currencySymbol}>{getSymbolFromCurrency(currentCurrency)}</span>
                <input
                  type="number"
                  value={Math.round(+budgetValues[0])}
                  onKeyDown={handleMinInputKeyDown}
                  min={budgetRange[0]}
                  max={budgetValues[1]}
                  onBlur={() => handleMinInputChange(parseFloat(String(budgetValues[0])))}
                  onChange={(e) => setBudgetValues([+e.target.value, budgetValues[1]])}
                  placeholder="Min"
                  style={{paddingLeft: `${23 + (getSymbolFromCurrency(currentCurrency)?.length || 0)*7}px` }}
                />
              </div>
              <div>—</div>
              <div className={styles.max}>
                <span className={styles.currencySymbol}>{getSymbolFromCurrency(currentCurrency)}</span>
                <input
                  type="number"
                  value={Math.round(+budgetValues[1])} //Math.round(+maxInputValue)
                  onKeyDown={handleMaxInputKeyDown}
                  min={budgetValues[0]}
                  max={budgetRange[1]}
                  onBlur={() => handleMaxInputChange(parseFloat(String(budgetValues[1])))}
                  onChange={(e) => setBudgetValues([budgetValues[0], +e.target.value])}
                  placeholder="Max"
                  style={{paddingLeft: `${23 + (getSymbolFromCurrency(currentCurrency)?.length || 0)*7}px` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </button>
  );
};

export default BudgetFilter;
